import React, { useState, useMemo, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useData from '../hooks/useData';
import useToast from '../hooks/useToast';
import GlassButton from '../components/ui/Button';
import ToolCategory from '../components/tools/ToolCategory';
import ToolItem from '../components/tools/ToolItem';
import ToolFormModal from '../components/modals/ToolFormModal';
import CategoryFormModal from '../components/modals/CategoryFormModal';
import { AddIcon, SearchIcon, ListIcon, GridIcon, StarIcon, ChevronDownIcon } from '../components/ui/Icons';
import ConfirmDeleteModal from '../components/modals/ConfirmDeleteModal';

const ToolsPage = () => {
    const {
        toolCategories, tools,
        handleSaveToolCategory, handleDeleteToolCategory,
        handleSaveTool, handleDeleteTool, handleUpdateTool,
        handleUpdateCategoryOrder
    } = useData();
    const { showToast } = useToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [openCategories, setOpenCategories] = useState({});
    const [isFavoritesOpen, setIsFavoritesOpen] = useState(true);

    const toggleCategory = (categoryId) => {
        setOpenCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);

    const [isToolModalOpen, setToolModalOpen] = useState(false);
    const [toolToEdit, setToolToEdit] = useState(null);

    const [itemToDelete, setItemToDelete] = useState({ type: null, data: null });

    const onEditTool = (tool) => {
        setToolToEdit(tool);
        setToolModalOpen(true);
    };

    const onDeleteTool = (id) => {
        const toolToDelete = tools.find(t => t.id === id);
        setItemToDelete({ type: 'tool', data: { id, name: toolToDelete?.name } });
    };

    const { favoriteTools, filteredCategories, getToolsForCategory, filteredToolIds } = useMemo(() => {
        const lowercasedFilter = searchTerm.toLowerCase();

        const filteredTools = searchTerm
            ? tools.filter(tool =>
                tool.name.toLowerCase().includes(lowercasedFilter) ||
                (tool.description && tool.description.toLowerCase().includes(lowercasedFilter))
              )
            : tools;

        const filteredToolIds = new Set(filteredTools.map(t => t.id));
        const favoriteTools = tools.filter(tool => tool.isFavorite);

        const filteredCategories = toolCategories
            .filter(category =>
                category.name.toLowerCase().includes(lowercasedFilter) ||
                tools.some(tool => tool.categoryId === category.id && filteredToolIds.has(tool.id))
            )
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        const getToolsForCategory = (categoryId) => {
            return filteredTools.filter(tool => tool.categoryId === categoryId);
        };

        return { favoriteTools, filteredCategories, getToolsForCategory, filteredToolIds };
    }, [toolCategories, tools, searchTerm]);

    const moveCategory = useCallback((dragIndex, hoverIndex) => {
        const reordered = [...filteredCategories];
        const [draggedItem] = reordered.splice(dragIndex, 1);
        reordered.splice(hoverIndex, 0, draggedItem);
        const orderedIds = reordered.map(c => c.id);
        handleUpdateCategoryOrder(orderedIds);
    }, [filteredCategories, handleUpdateCategoryOrder]);

    const handleMoveTool = (toolId, newCategoryId) => {
        const tool = tools.find(t => t.id === toolId);
        if (tool && tool.categoryId !== newCategoryId) {
            handleUpdateTool(toolId, { categoryId: newCategoryId });
            showToast(`'${tool.name}' movido de categoria!`);
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="p-4 space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-3xl font-bold text-white">Ferramentas Úteis</h2>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Buscar ferramentas..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-64 bg-white/10 text-white rounded-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <SearchIcon size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        </div>
                        <div className="flex gap-1 rounded-full bg-black/20 p-1">
                            <button onClick={() => setViewMode('list')} className={`p-2 rounded-full transition-colors ${viewMode === 'list' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}><ListIcon size={16}/></button>
                            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}><GridIcon size={16}/></button>
                        </div>
                        <GlassButton onClick={() => { setCategoryToEdit(null); setCategoryModalOpen(true); }} variant="minimal">Nova Categoria</GlassButton>
                        <GlassButton onClick={() => { setToolToEdit(null); setToolModalOpen(true); }} className="!bg-indigo-500"><AddIcon size={16}/> Adicionar Ferramenta</GlassButton>
                    </div>
                </div>

                <div className="space-y-6">
                    {(favoriteTools.length > 0 && searchTerm === '') || (favoriteTools.some(t => filteredToolIds.has(t.id)) && searchTerm !== '') ? (
                        <div className={`bg-black/20 p-4 rounded-lg`}>
                            <button onClick={() => setIsFavoritesOpen(!isFavoritesOpen)} className="w-full flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-yellow-400 flex items-center gap-2"><StarIcon size={20}/> Favoritos</h3>
                                <ChevronDownIcon size={20} className={`transition-transform duration-300 ${isFavoritesOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isFavoritesOpen ? 'max-h-[1000px]' : 'max-h-0'}`}>
                                <div className={viewMode === 'list' ? 'space-y-2' : 'grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-4'}>
                                    {favoriteTools.filter(t => filteredToolIds.has(t.id)).map(tool => (
                                        <ToolItem key={tool.id} tool={tool} onEdit={onEditTool} onDelete={onDeleteTool} viewMode={viewMode} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {filteredCategories.map((category, index) => {
                        const toolsForCategory = getToolsForCategory(category.id);
                        if (searchTerm && toolsForCategory.length === 0 && !category.name.toLowerCase().includes(searchTerm.toLowerCase())) return null;
                        return (
                            <ToolCategory
                                key={category.id}
                                index={index}
                                moveCategory={moveCategory}
                                category={category}
                                tools={toolsForCategory}
                                isOpen={!!openCategories[category.id]}
                                onToggle={() => toggleCategory(category.id)}
                                onEditCategory={(cat) => { setCategoryToEdit(cat); setCategoryModalOpen(true); }}
                                onDeleteCategory={(id) => setItemToDelete({ type: 'category', data: { id, name: category.name } })}
                                onEditTool={onEditTool}
                                onDeleteTool={onDeleteTool}
                                onMoveTool={handleMoveTool}
                                viewMode={viewMode}
                            />
                        );
                    })}
                </div>

                <CategoryFormModal
                    isOpen={isCategoryModalOpen}
                    onClose={() => setCategoryModalOpen(false)}
                    onSave={handleSaveToolCategory}
                    categoryToEdit={categoryToEdit}
                />
                <ToolFormModal
                    isOpen={isToolModalOpen}
                    onClose={() => setToolModalOpen(false)}
                    onSave={handleSaveTool}
                    toolToEdit={toolToEdit}
                />
                <ConfirmDeleteModal
                    item={itemToDelete.data}
                    type={itemToDelete.type}
                    onCancel={() => setItemToDelete({ type: null, data: null })}
                    onConfirm={() => {
                        if (itemToDelete.type === 'category') {
                            handleDeleteToolCategory(itemToDelete.data.id);
                        } else if (itemToDelete.type === 'tool') {
                            handleDeleteTool(itemToDelete.data.id);
                        }
                        setItemToDelete({ type: null, data: null });
                    }}
                />
            </div>
        </DndProvider>
    );
};

export default ToolsPage;